import {
  Stack,
  Typography
} from "@mui/material";
import {
  alpha,
  useTheme
} from "@mui/material/styles";
import { CircleCheck, TriangleAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyledModal } from "./UnsubscribeEditionConfirmModal.styles";
import { useUnsubscribeEditionMutation } from "../../queries";
import { ButtonDigited } from "../Button/Button.variants";

type ModalState = "confirm" | "success" | "error";

export function UnsubscribeEditionConfirmModal({
  description,
  editionId,
  objectId,
  onClose,
  onSuccess,
  onUnsubscribingChange,
  open,
  showWarningIcon = false,
  title,
  closeOnSuccess = false
}: {
  description: string
  editionId: number
  objectId: number
  onClose: () => void
  onSuccess: () => void
  onUnsubscribingChange?: (isUnsubscribing: boolean) => void
  open: boolean
  showWarningIcon?: boolean
  title: string
  closeOnSuccess?: boolean
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [modalState, setModalState] = useState<ModalState>("confirm");
  const [internalOpen, setInternalOpen] = useState(false);

  const {
    mutate: unsubscribeMutation,
    isPending: isUnsubscribing
  } = useUnsubscribeEditionMutation({
    editionId,
    objectId
  });

  useEffect(() => {
    if (open) {
      setInternalOpen(true);
    }
  }, [open]);

  useEffect(() => {
    if (modalState === "success" || modalState === "error") {
      setInternalOpen(true);
    }
  }, [modalState]);

  const handleClose = () => {
    setModalState("confirm");
    setInternalOpen(false);
    onClose();
  };

  const handleConfirm = () => {
    setInternalOpen(false);
    onUnsubscribingChange?.(true);
    
    unsubscribeMutation(undefined, {
      onError: () => {
        onUnsubscribingChange?.(false);
        setModalState("error");
      },
      onSuccess: () => {
        onUnsubscribingChange?.(false);
        if (closeOnSuccess) {
          onSuccess();
          handleClose();
          return;
        }
        setModalState("success");
      }
    });
  };

  const handleContinue = () => {
    if (modalState === "success") onSuccess();
    handleClose();
  };
  return (
    <StyledModal
      aria-labelledby="modal-unsubscribe"
      open={ internalOpen }
      onClose={ handleClose }
      slotProps={ {
        backdrop: {
          sx: {
            background: theme.customColors.backgroundCard,
            border: `2pxsolid ${theme.customColors.border}`
          }
        }
      } }
    >
      <Stack
        alignItems="center"
        gap="1rem"
        sx={ { "&:focus-visible": {
          outline: "none"
        } } }
      >
        { modalState === "confirm" && (
          <>
            <Stack
              alignItems="center"
              gap="0.5rem"
            >
              { showWarningIcon && (
                <TriangleAlert
                  color={ theme.customColors.exploreCardText }
                  size="3rem"
                />
              ) }
              <Typography
                aria-labelledby="modal-modal-title"
                color={ theme.customColors.exploreCardText }
                variant="h2"
                fontSize="1.5rem"
                lineHeight="1.8rem"
                fontWeight={ 600 }
                sx={ { textAlign: "center" } }
              >
                { title }
              </Typography>
              <Typography
                aria-describedby="modal-modal-description"
                color={ theme.customColors.exploreCardText }
                fontSize="0.875rem"
                lineHeight="1rem"
                sx={ { textAlign: "center", whiteSpace: "pre-line" } }
              >
                { description }
              </Typography>
            </Stack>
            <Stack
              direction="row"
              gap={ 2 }
              sx={ { width: "100%" } }
            >
              <ButtonDigited
                typology={ "secondary" }
                text={ t("annul") }
                onClick={ handleClose }
                sx={ { flex: 1 } }
              />
              <ButtonDigited
                typology={ "primary" }
                text={ t("yes") }
                disabled={ isUnsubscribing }
                onClick={ handleConfirm }
                sx={ {
                  flex: 1
                } }
              />
            </Stack>
          </>
        ) }

        { modalState === "success" && (
          <Stack
            alignItems="center"
            gap="1rem"
          >
            <CircleCheck
              color={ theme.customColors.exploreCardText }
              size="3rem"
            />
            <Typography
              aria-labelledby="modal-modal-title"
              color={ theme.customColors.exploreCardText }
              variant="h2"
              fontSize="1.5rem"
              lineHeight="1.8rem"
              fontWeight={ 600 }
              sx={ { textAlign: "center" } }
            >
              { title }
            </Typography>
            <Typography
              aria-describedby="modal-modal-description"
              color={ theme.customColors.exploreCardText }
              fontSize="0.875rem"
              lineHeight="1rem"
              sx={ { textAlign: "center", whiteSpace: "pre-line" } }
            >
              { t("unsubscribe_success_message") }
            </Typography>
            <ButtonDigited
              typology={ "primary" }
              text={ t("continue") }
              onClick={ handleContinue }
              sx={ {
                ":hover": { backgroundColor: alpha(theme.customColors.backgroundPrimaryCta, 0.9) }
              } }
            />
          </Stack>
        ) }

        { modalState === "error" && (
          <Stack
            alignItems="center"
            gap="1rem"
          >
            <TriangleAlert
              color={ theme.customColors.exploreCardText }
              size="3rem"
            />
            <Typography
              aria-describedby="modal-modal-description"
              color={ theme.customColors.exploreCardText }
              fontSize="0.875rem"
              lineHeight="1rem"
              sx={ { textAlign: "center", whiteSpace: "pre-line" } }
            >
              { t("unsubscribe_error_message") }
            </Typography>
            <ButtonDigited
              typology={ "primary" }
              text={ t("continue") }
              onClick={ handleContinue }
              sx={ {
                ":hover": { backgroundColor: alpha(theme.customColors.backgroundPrimaryCta, 0.9) }
              } }
            />
          </Stack>
        ) }
      </Stack>
    </StyledModal>
  );
}
